import { TextFieldModule } from '@angular/cdk/text-field';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { ContactDto, CustomerContactListItemDto, CustomerResponse, DealCategory, DealCommentDto, DealDto, DealReminderDto, DealStageDetailDto, DealStageDto, EnumViewModel, QuoteFileDto, UserDto } from 'app/api/models';
import { ContactService, CustomerService, DealService, EmployeeService, ExcelService } from 'app/api/services';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import 'material-design-inspired-color-picker';
import { GenericCommentDto } from 'app/core/components/comments/comments.component';
import { SharedComponentsModule } from 'app/core/components/shared-components.module';
import { isNumber } from 'lodash';
import { AddItemDialogComponent } from 'app/core/components/add-item-dialog/add-item-dialog.component';
import { AppAuthService } from 'app/api/services/cookie-jwt-auth/app-auth.service';
import { TimeUtil } from 'app/core/utils/time-utils';
import { UploadFilePopupComponent } from 'app/views/admin/installation/documentations/upload-file-popup/upload-file-popup.component';
import { AppLoaderService } from 'app/core/services/app-loader/app-loader.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
    selector       : 'scrumboard-card-details',
    templateUrl    : './details.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone     : true,
    imports        : [
        MatButtonModule, 
        MatIconModule, 
        FormsModule, 
        ReactiveFormsModule, 
        MatFormFieldModule, 
        MatInputModule, 
        MatOptionModule,
        TextFieldModule, 
        NgClass, 
        NgIf, 
        MatDatepickerModule, 
        NgFor, 
        MatCheckboxModule, 
        DatePipe, 
        MatTooltipModule, 
        MatMenuModule,
        MatSelectModule,
        SharedComponentsModule,
        MatAutocompleteModule,
    ],
    styleUrls: ['./details.component.scss'],
})
export class ScrumboardCardDetailsComponent implements OnInit, OnDestroy
{
    dealId: number;
    deal: DealDto = {};
    cardForm: UntypedFormGroup;

    currentUserId: number;
    hasReminder: boolean = false;
    validatorsOn: boolean = false;

    public genericComments: GenericCommentDto[];
    public lostReasonComments: GenericCommentDto[];
    files: QuoteFileDto[] = [];

    stages: DealStageDto[] = [];
    contacts: CustomerContactListItemDto[] = [];
    companies: CustomerResponse[] = [];
    filteredCompanies: CustomerResponse[] = [];
    selectedCompanyName: string = '';
    users: UserDto[] = [];

    lead: ContactDto;

    public emailExpanded: boolean = false;
    public partsExpanded: boolean = false;
    public commentsExpanded: boolean = false;
    public lostReasonExpanded: boolean = false;
    public filesExpanded: boolean = false;

    public categories = Object.values(DealCategory).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getCategoryName(+x), value: <DealCategory>x});
    
    private dealLostReasons: EnumViewModel[] = [
        'Price',
        'Lead Time',
        'Customer Service',
        'Not the Right Product',
        'Gone with Competitor',
        'Found Another Solution',
        'Project not Going Ahead',
        'Hire Instead',
        'Ordered on Another Quote',
        'Budgetary Quote',
        "Customer not Responded",
    ].map((x, i) => <EnumViewModel> {name: x, value: i});

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        public matDialogRef: MatDialogRef<ScrumboardCardDetailsComponent>,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        private employeeService: EmployeeService,
        public appAuth: AppAuthService,
        private dealService: DealService,
        private customerService: CustomerService,
        private contactService: ContactService,
        private excelService: ExcelService,
        private _fuseConfirmationService: FuseConfirmationService,
        private dialog: MatDialog,
        private loader: AppLoaderService,
        private datePipe: DatePipe,
        @Inject(MAT_DIALOG_DATA) public data: ScrumboardCardDetailsComponent
    )
    {
        this.dealId = data.dealId;
        if(!data.dealId && data.deal) {
            this.deal = data.deal;
            this.genericComments = [];
            this.lostReasonComments = [];
        }
    }

    ngOnInit(): void
    {
        this.currentUserId = this.appAuth.getUser()?.id;

        // Prepare the card form
        this.cardForm = this._formBuilder.group({
            id                  : [null],
            name                : ['',      Validators.required],
            customerReqNo       : ['',      Validators.required],
            stageId             : [null,    Validators.required],
            sentDate            : [null],
            closeDate           : [null],
            companyId           : [null],
            contactId           : [null],
            // leadId              : [null],
            value               : [0,       Validators.required],
            partsDescription    : [''],
            dealCategories      : [[]],
            comments            : [[]],
            userId              : [null,    Validators.required],
            notes               : [''],
        });

        this.loadFiles();
        this.loadDealStages();
        this.loadUsers();

        this.loadCompaniesContactsAndDeal();


    }

    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    loadDeal() {
        if(!this.dealId && this.deal) {
            this.cardForm.patchValue({
                stageId             : this.deal.stageId,
                dealCategories      : this.deal.dealCategories ?? [],
            });

            this.splitComments(this.deal.comments);

            if(this.deal.leadId)
                this.loadLead();

            return;
        }
        
        if(!this.dealId) return;

        this.dealService.apiDealDealGet$Json$Response({dealId: this.dealId}).subscribe(res => {
            if(!res.body.data) return;

            this.deal = res.body.data;
            
            // EMAIL SYSTEM DIAGNOSTICS - Deal Details
            console.log('=== DEAL DETAILS EMAIL DIAGNOSTICS ===');
            console.log(`Deal: ${this.deal.name} (ID: ${this.deal.dealId})`);
            console.log('Email trails count:', this.deal.emailTrails?.length || 0);
            
            if (this.deal.emailTrails && this.deal.emailTrails.length > 0) {
                console.log('Email trails found:');
                this.deal.emailTrails.forEach((email, index) => {
                    console.log(`  Email ${index + 1}:`);
                    console.log(`    Subject: ${email.subject}`);
                    console.log(`    From: ${email.fromName} <${email.fromEmail}>`);
                    console.log(`    Date: ${email.date}`);
                    console.log(`    Outlook ID: ${email.outlookId}`);
                    console.log(`    Body preview: ${email.bodyPreview?.substring(0, 100)}...`);
                });
            } else {
                console.log('No email trails found for this deal');
            }
            console.log('=== END DEAL DETAILS EMAIL DIAGNOSTICS ===');
            
            this.selectedCompanyName = this.deal.companyId ?
                (this.companies.find(x => x.customerId == this.deal.companyId)?.name ?? '') : '';

            this.splitComments(this.deal.comments);

            this.loadHasGotReminder();
            
            if(this.deal.leadId)
                this.loadLead();

            this.setForm();
        });
    }

    loadLead() {
        this.contactService.apiContactGetContactPost$Json$Response({id: this.deal.leadId}).subscribe(res => {
            if(!res?.body?.data) return;

            this.lead = res.body.data;
            this.cdr.detectChanges();
        });
    }

    setForm() {
        // Fill the form
        this.cardForm.setValue({
            id                  : this.deal.dealId,
            name                : this.deal.name,
            customerReqNo       : this.deal.customerReqNo,
            stageId             : this.deal.stageId,
            sentDate            : this.deal.sentDate,
            closeDate           : this.deal.closeDate,
            companyId           : this.deal.companyId,
            contactId           : this.deal.contactId,
            // leadId              : this.deal.leadId,
            value               : this.deal.value,
            partsDescription    : this.deal.partsDescription,
            dealCategories      : this.deal.dealCategories,
            comments            : this.deal.comments,
            userId              : this.deal.userId,
            notes               : this.deal.notes,
        });
        this.updateValidators();

        this.cdr.detectChanges();
    }

    loadCompaniesContactsAndDeal() {
        this.customerService.apiCustomerSearchAllGet$Json$Response().subscribe(res => {
            if(!res?.body?.data) return;

            this.companies = res.body.data;
            this.filteredCompanies = this.companies;

            this.loadContacts();
        });
    }

    loadContacts() {
        this.customerService.apiCustomerSearchContactsGet$Json$Response().subscribe(res => {
            if(!res?.body?.data) return;

            this.contacts = res.body.data;
            this.cdr.detectChanges();

            this.loadDeal();
        });
    }

    loadDealStages() {
        this.dealService.apiDealStagesGet$Json$Response().subscribe(res => {
            if(!res?.body?.data) return;

            this.stages = res.body.data;
            this.cdr.detectChanges();
        });
    }

    loadUsers() {
      this.employeeService.apiEmployeeSearchEmployeeUsersGet$Json$Response({archived: false}).subscribe(res => {
        if(!res?.body.data) return;
  
        this.users = res.body.data;
        this.cdr.detectChanges();
      });
    }
    
    getCategoryName(cat: number) {
        switch(cat) {
            case DealCategory.StandardProducts: return 'Standard';
            case DealCategory.CustomProducts: return 'Custom';
            case DealCategory.CableWorkingGear: return 'Cable Working Gear';
            case DealCategory.MarineSales: return 'Marine';
            case DealCategory.NewLead: return 'New Lead';
            case DealCategory.RepeatCustomProduct: return 'Repeat Custom';
            default: return DealCategory[cat].toString();
        }
    }

    getCompanyContacts() {
        var companyId = this.cardForm.controls['companyId'].value;

        return (companyId ? this.contacts?.filter(x => x.customerId == companyId) : this.contacts) ?? [];
    }

    getContactEmail() {
        var contactId = this.cardForm.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.email ?? '';
    }

    getContactPhoneNumber() {
        var contactId = this.cardForm.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.phoneNumber ?? '';
    }

    getContactDepartment() {
        var contactId = this.cardForm.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.department ?? '';
    }

    getLeadEmail() {
        var emails = this.lead?.emails;
        return emails?.length ? emails[0].email : '';
    }

    getLeadPhoneNumber() {
        var phones = this.lead?.phoneNumbers;
        return phones?.length ? phones[0].phoneNumber : '';
    }

    getQuoteCreated() {
        var date = this.deal?.createdUTC;
        if(!date) return '';

        return this.datePipe.transform(date, 'dd/MM/yy HH:mm');
    }

    public updateContact(event: any) {
        this.deal.contactId = event?.value;
        this.cardForm.controls['userId']?.setValue(event?.value);

        this.cdr.detectChanges();
    }

    isWonStage = (stage: DealStageDetailDto) => stage.name.toLowerCase().includes('won');
    isLostStage = (stage: DealStageDetailDto) => stage.name.toLowerCase().includes('lost');
    isDealLost = () => this.stages.find(x => x.stageId == this.deal?.stageId)?.name.toLowerCase().includes('lost');
    isProspectsStage = (stageId: number) => this.stages.find(x => x.stageId == stageId)?.name.toLowerCase().includes('prospects');
    isFollowUpStage = (stageId: number) => this.stages.find(x => x.stageId == stageId)?.name.toLowerCase().includes('follow-up');
    isNegotiationStage = (stageId: number) => this.stages.find(x => x.stageId == stageId)?.name.toLowerCase().includes('negotiation');
    isProposalStage = (stageId: number) => this.stages.find(x => x.stageId == stageId)?.name.toLowerCase().includes('proposal');

    archiveCard() {
        const confirmation = this._fuseConfirmationService.open({
            title  : 'Delete Quote',
            message: `Are you sure you want to remove this quote?`,
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });
    
        confirmation.afterClosed().subscribe((result) => {
          if (result === 'confirmed') {
            
            this.dealService.apiDealArchiveDealDelete$Json$Response({dealId: this.deal.dealId}).subscribe(res => {
                if(!res?.body?.data) return;

                this.close(true);
            });
          }
        });
    }

    updateValidators() {
        var value = +this.cardForm.controls['value'].value;

        var fieldNames = this.deal?.isLeadQuote ? ['sentDate', 'dealCategories'] : [
            'sentDate',
            'companyId',
            'contactId',
            'dealCategories',
        ];

        this.validatorsOn = value >= 2000;
        console.log("updating validators", this.validatorsOn ? 'on' : 'off');

        for(var fieldName of fieldNames) {
            if(this.validatorsOn) 
                this.cardForm.get(fieldName)?.setValidators([
                    Validators.required
                ]);
            else 
                this.cardForm.get(fieldName)?.clearValidators();
            
            this.cardForm.get(fieldName)?.updateValueAndValidity();
        }
    }
    
    filterCompanies(event: any) {
        var text = event.target.value;
        this.filteredCompanies = this.companies.filter(m => m.name.toLowerCase().includes(text.toLowerCase()));
        this.cdr.detectChanges();
    }

    onCompanySelected(company: CustomerResponse) {
        this.cardForm.controls["companyId"].setValue(company.customerId);
        this.selectedCompanyName = company.name;
        this.cdr.detectChanges();
    }

    /**
     * Check if the given date is overdue
     */
    isOverdue(date: string): boolean
    {
        return DateTime.fromISO(date).startOf('day') < DateTime.now().startOf('day');
    }
    

    getTimeSinceLastEmail() {
        var date = this.isNegotiationStage(this.deal?.stageId) || this.isProposalStage(this.deal?.stageId) ?
            this.getLastEmailDate() :
            null;

        return !date ? '0 hrs' : TimeUtil.getDaysIntervalText(date, TimeUtil.getNowUTC());
    }

    getLastEmailDate() {
        //get latest of either date last emailed or swimlane moved

        if(this.deal?.lastEmailDate && this.deal?.dateSinceSwimlaneChange)
            return TimeUtil.getBiggestDate(new Date(this.deal.lastEmailDate), new Date(this.deal.dateSinceSwimlaneChange));

        if (this.deal?.lastEmailDate)
            return new Date(this.deal.lastEmailDate);
        
        if (this.deal?.dateSinceSwimlaneChange)
            return new Date(this.deal.dateSinceSwimlaneChange);
        
        return null;
    }

    async save(){
        // update card object
        var formValue = this.cardForm.value;
        
        this.deal.dealCategories = formValue.dealCategories;

        //if stage changed to lost, require a reason and comment otherwise don't allow save
        if(this.dealId && this.deal.stageId != formValue.stageId) {
            var newStage = this.stages.find(x => x.stageId == formValue.stageId);
            var oldStage = this.stages.find(x => x.stageId == this.deal.stageId);

            var isLost = newStage.name.toLowerCase().includes('lost');
            var isWon = this.isWonStage(newStage);
            var isProspect = this.isProspectsStage(newStage.stageId);
            var wasProspect = this.isProspectsStage(oldStage.stageId);
            var wasFollowUp = this.isFollowUpStage(oldStage.stageId);
            
            var movedIntoNegotiation = this.isNegotiationStage(newStage.stageId) && !this.isNegotiationStage(oldStage.stageId);
            var movedIntoProposal = this.isProposalStage(newStage.stageId) && !this.isProposalStage(oldStage.stageId);
            
            if(isLost) {
                //if moved to lost, require user to input reason
                var dialog = this.dialog.open(AddItemDialogComponent, {
                    data: {
                        title: `Enter Reason for Deal Lost`,
                        items: [
                            {name: 'reason', type: 'select', options: this.dealLostReasons, initialValue: 0},
                            {name: 'comment', type: 'textarea', initialValue: ''},
                        ],
                    }
                });
            
                var dialogRes = await dialog.afterClosed().toPromise();
                if (!dialogRes)
                    return;

                var lostReasonName = this.dealLostReasons[dialogRes.reason].name;

                if(lostReasonName == 'Customer not Responded') {
                    //additional prompt
                    const confirmation = this._fuseConfirmationService.open({
                        title  : 'Customer not responded',
                        message: `Have you called the customer?`,
                        actions: {
                            confirm: {
                                label: 'Yes',
                            },
                            cancel: {
                                label: 'No',
                            }
                        },
                    });
    
                    var confirmationRes = await confirmation.afterClosed().toPromise();
                    if (confirmationRes !== 'confirmed')
                        return;
                }

                //set reason and add comment
                var comment = <DealCommentDto>{
                    dealId: this.dealId,
                    userId: this.appAuth.getUser().id,
                    text: `Deal Lost: ${lostReasonName}\n\n${dialogRes.comment}`,
                    timeStamp: new Date().toISOString(),
                };
            
                await this.dealService.apiDealAddCommentPost$Json$Response({body: comment}).toPromise();
                
                if(wasFollowUp || wasProspect) {
                    //exclude from sales KPIs but keep on sales dash
                    this.deal.excludeFromSalesKpis = true;
                }
            }
            else if(wasProspect) {
                //requires email chain or comment
                if(!this.deal.emailTrails?.length && this.deal.comments?.length) {
                    return;
                }
            }
            else if(wasFollowUp && !isProspect && this.deal.dealCategories?.includes(DealCategory.NewLead)) {
                //requires quote upload and adds lead to customer DB
                await this.openQuoteUpload();
                
                if(!this.deal.isFromQuoteUpload)
                    return;

                //add lead to customer DB
                if(this.lead?.isLead)
                    this.transferToContacts(this.lead);
            }

            // if(movedIntoNegotiation || movedIntoProposal)
            this.deal.dateSinceSwimlaneChange = new Date().toISOString();
        }

        this.deal.name = formValue.name;
        this.deal.customerReqNo = formValue.customerReqNo;
        this.deal.stageId = formValue.stageId;
        this.deal.companyId = formValue.companyId;
        this.deal.contactId = formValue.contactId;
        // this.deal.leadId = formValue.leadId;
        this.deal.sentDate = formValue.sentDate;
        this.deal.closeDate = formValue.closeDate; //todo
        this.deal.partsDescription = formValue.partsDescription;
        this.deal.comments = formValue.comments;
        this.deal.value = formValue.value;
        this.deal.userId = formValue.userId;
        this.deal.notes = formValue.notes;

        // Update the card on the server
        if(this.dealId)
            await this.dealService.apiDealUpdateDealPut$Json$Response({body: this.deal}).toPromise();
        else
            await this.dealService.apiDealAddDealPost$Json$Response({body: this.deal}).toPromise();

        // Mark for check
        this.cdr.markForCheck();

        this.close(true);
    }

    private transferToContacts(lead: ContactDto) {
        this.customerService.apiCustomerAddCustomerAndContactFromLeadPost$Json$Response({body: lead}).subscribe(res => {
            if(!res?.body?.data) return;

            lead.isLead = false;
            this.contactService.apiContactUpdateContactPost$Json$Response({body: lead}).subscribe();
        });
    }

    close(value: boolean = false){
        this.matDialogRef.close(value);
    }

    loadHasGotReminder() {
        if(!this.dealId) return;

        this.dealService.apiDealGetRemindersGet$Json$Response({dealId: this.dealId, userId: this.currentUserId}).subscribe(res => {
            this.hasReminder = res?.body?.data?.length > 0;
            this.cdr.detectChanges();
        });
    }

    async setReminder() {
        //retrieve reminder for current user if exists
        var currentReminder: DealReminderDto;

        if(this.dealId) {
            var res = await this.dealService.apiDealGetRemindersGet$Json$Response({dealId: this.dealId, userId: this.currentUserId}).toPromise();
            
            if(res?.body?.data?.length)
                currentReminder = res.body.data[0];
        }

        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: "Set Reminder",
                items: [
                    {name: 'remind me on', propertyName: 'reminderDate', type: 'date', initialValue: currentReminder?.reminderDate},
                ],
            }
        });
    
        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            var request = <DealReminderDto> {
                dealId: this.dealId,
                userId: this.currentUserId,
                reminderDate: TimeUtil.setTo1amUTC(new Date(dialogRes.reminderDate)).toISOString(),
                dealReminderId: currentReminder?.dealReminderId,
            };

            if(currentReminder)
                this.dealService.apiDealUpdateReminderPut$Json$Response({body: request}).subscribe();
            else
                this.dealService.apiDealAddReminderPost$Json$Response({body: request}).subscribe(res => {
                    if(!res?.body?.data) return;
                    this.hasReminder = true;
                });
        });
    }

    //quote upload
    async openQuoteUpload() {
        var dialog = this.dialog.open(UploadFilePopupComponent, {
            data: {
                allowedTypes: ['xls', 'xlsx', 'xlsm'],
                allowedTypesDescription: 'excel',
                multiple: false,
                title: 'Upload Quote from M1',
            },
            autoFocus: false,
        });

        var dialogRes = await dialog.afterClosed().toPromise();
        if (!dialogRes) return;

        await this.uploadQuote(dialogRes);
    }
    
    async uploadQuote(files: FileList | File[]) {
        var newFiles = Array.from(files);

        this.loader.open();

        if(newFiles.length == 0) return;

        var file = newFiles[0];

        var imageFormat = file.name!.split(".").pop()?.trim()?.toLowerCase();
        var blob: Blob = file;
        var name: string = file.name;

        var res = await this.excelService.apiExcelProcessExistingQuotePost$Json$Response({dealId: this.dealId, body: { formFile: blob }}).toPromise();

        if(res?.body?.errors?.length > 0) {
            
            if(res?.body?.succeeded == false || !res?.body?.data) {
                //show dialog
                this._fuseConfirmationService.open({
                    title  : `Problems encountered with file "${name}"` ,
                    message: '<br/>This file has the following errors:<br/><br/>' +
                                res?.body?.errors.join('<br/>') +
                                '<br/><br/>Unfortunately, the errors in this file mean that extracting quote info is not possible.',
                    actions: {
                        confirm: {
                            label: "Ok",
                        },
                        cancel: {
                            show: false,
                        }
                    },
                });
                return;
            }
            
            //potentially flag up the errors on partial process? - ignore for now as gets user final sign-off on this dialog anyway
        }
        this.loader.close();

        if(!res?.body?.data) return;

        //doesn't automatically update the deal - put the new info in the form and let the user decide whether to save or not
        this.deal = res.body.data;
        this.setForm();
        this.cardForm.markAsDirty();
    }

    // Files
    loadFiles() {
        if(!this.dealId) return;

        this.dealService.apiDealGetFilesGet$Json({ dealId: this.dealId }).subscribe(res => {
            if(!res?.data) return;

            this.files = res.data;

            this.cdr.detectChanges();
        });
    }

    async openFileUpload(){
        var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});
  
        dialog.afterClosed().subscribe((dialogRes) => {
          if (!dialogRes) return;
  
          this.uploadImages(dialogRes);
        });    
    }

    deleteFile(file: QuoteFileDto, event: any){
        event.stopPropagation();

        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
         title  : "Delete File",
         message: 'Are you sure you want to delete this file?',
         actions: {
             confirm: {
               label: "Delete",
             },
         },
       });
   
       // Subscribe to the confirmation dialog closed action
       confirmation.afterClosed().subscribe((result) => {
         // If the confirm button pressed...
         if ( result === 'confirmed' )
         {
           this.dealService.apiDealDeleteFileFileIdDelete$Json({ fileId: file?.quoteFileId }).subscribe((res) => {
                if(!res) return;
    
                this.loadFiles();
           });
         }
       });
     }
  
    async uploadImages(files: FileList) {
        if (!this.dealId) return;
        
        var newFiles = Array.from(files);

        newFiles
            .forEach((file) => {
                var imageFormat = file.name!.split(".").pop()?.trim()?.toLowerCase();
                var blob: Blob = file;
                var name: string = file.name;

                this.dealService.apiDealUploadFileDealIdPost$Json({ 
                    dealId: this.dealId,
                    filename: name,
                    isBom: false,
                    body: { formFile: blob },
                }).subscribe((res) => {
                    this.loadFiles();
                });
            });
    }

    download(file: QuoteFileDto){
        this.dealService.apiDealDownloadFileFileIdGet({ fileId: file.quoteFileId }).subscribe((res) => {
            if(!res) return;

            // Convert the response to a Blob
            const blob = new Blob([res], { type: 'application/octet-stream' });

            // Create a link element
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // if the items name doesnt end with the file extension, add it
            if(!file?.name.endsWith(file?.type)){
                file.name += file?.type;
            }

            // Set the download attribute of the link to the desired file name
            link.download = file.name;
            // link.target = '_blank';

            // window.open(url);

            // Simulate a click on the link
            link.click();

            // Release the reference to the blob
            window.URL.revokeObjectURL(url);
        });
    }


    //Comments
    getGenericComments = (comments: DealCommentDto[]): GenericCommentDto[] =>
        comments?.map(x => <GenericCommentDto>{
            id: x.dealCommentId,
            parentId: x.dealId,
            text: x.text,
            userId: x.userId,
            timeStamp: new Date(x.timeStamp)});

    splitComments(comments: DealCommentDto[]) {
        if (!comments) {
            this.genericComments = [];
            this.lostReasonComments = [];
            return;
        }

        // Separate "Deal Lost:" comments from regular comments
        const lostComments = comments.filter(x => x.text?.startsWith('Deal Lost:'));
        const regularComments = comments.filter(x => !x.text?.startsWith('Deal Lost:'));

        this.lostReasonComments = this.getGenericComments(lostComments);
        this.genericComments = this.getGenericComments(regularComments);
    }
  
    addComment(comment: GenericCommentDto) {
        var dealComment = <DealCommentDto>{
            dealId: comment.parentId,
            userId: comment.userId,
            text: comment.text,
            timeStamp: comment.timeStamp.toISOString(),
        };
  
        this.dealService.apiDealAddCommentPost$Json$Response({body: dealComment}).subscribe(res => {
            if(!res?.body?.data) return;
    
            this.deal.comments.push(res.body.data);
            
            this.splitComments(this.deal.comments);
            
            this.cdr.detectChanges();
        });
    }
  
    updateComment(comment: GenericCommentDto) {
      var dealComment = <DealCommentDto>{
          dealCommentId: comment.id,
          dealId: comment.parentId,
          userId: comment.userId,
          text: comment.text,
          timeStamp: comment.timeStamp.toISOString(),
      };
  
      this.dealService.apiDealUpdateCommentPut$Json$Response({body: dealComment}).subscribe(res => {
          if(!res?.body?.data) return;
  
          var dealToChange = this.deal.comments.find(x => x.dealCommentId == dealComment.dealCommentId);
          dealToChange.text = dealComment.text;
          dealToChange.timeStamp = dealComment.timeStamp;
          
          this.splitComments(this.deal.comments);

          this.cdr.detectChanges();
      });
    }
  
    removeComment(id: number) {
      this.dealService.apiDealDeleteCommentDelete$Json$Response({commentId: id}).subscribe(res => {
          if(!res?.body?.data) return;
  
          var indexToRemove = this.deal.comments.findIndex(x => x.dealCommentId == id);
          this.deal.comments.splice(indexToRemove, 1);
          
          this.splitComments(this.deal.comments);
          
          this.cdr.detectChanges();
      });
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Read the given file for demonstration purposes
     *
     * @param file
     */
    private _readAsDataURL(file: File): Promise<any>
    {
        // Return a new promise
        return new Promise((resolve, reject) =>
        {
            // Create a new reader
            const reader = new FileReader();

            // Resolve the promise on success
            reader.onload = (): void =>
            {
                resolve(reader.result);
            };

            // Reject the promise on error
            reader.onerror = (e): void =>
            {
                reject(e);
            };

            // Read the file as the
            reader.readAsDataURL(file);
        });
    }
}
